import { BehaviorSubject } from 'rxjs';
import { easing } from 'ts-easing';

import { prettyPlayerId } from 'app/core/utilities/user-util';
import { Timeout, WindowWithWebkitAudio } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';

import { INITIAL_MUSIC_VOLUME, MAX_DISTANCE_V1, MIN_DISTANCE_V1 } from './constants';
import {
  DEFAULT_PARTICIPANT_MAX_VOLUME_PRESENTATION,
  DEFAULT_PRESENTER_VOLUME,
  FAR_VOLUME,
  MID_VOLUME,
  NEAR_VOLUME,
  NO_GROUP_FAR_VOLUME,
  NO_GROUP_MID_VOLUME,
  NO_GROUP_NEAR_VOLUME,
} from './constants/volume';
import { DEFAULT_MAX_VOLUME } from './constants/volume';
import { SceneService } from './services/scene.service';
import { calculateDistance } from './services/subscription/subscription.helper';
import { Player } from './social-pixi-canvas/player';
import SocialStateHelper from './social-state-machine/social-state-helper';
import SocialStateMachine from './social-state-machine/social-state-machine';

export default class SoundControl extends Loggable {
  public audioContext?: AudioContext;

  private _audioElement: HTMLAudioElement;
  private _eventHostVolume = DEFAULT_PRESENTER_VOLUME;
  private _musicVolume = INITIAL_MUSIC_VOLUME;
  private _previousMusicVolume = INITIAL_MUSIC_VOLUME;
  private _musicAutoReducedForGroup = false;

  private _participantMaxVolume = DEFAULT_PARTICIPANT_MAX_VOLUME_PRESENTATION;
  private isEventInProgress = false;

  private musicVolumeChanged = new BehaviorSubject<number>(INITIAL_MUSIC_VOLUME);
  public musicVolumeChanged$ = this.musicVolumeChanged.asObservable();
  private eventHostVolumeChanged = new BehaviorSubject<number>(this._eventHostVolume);
  public eventHostVolumeChanged$ = this.eventHostVolumeChanged.asObservable();
  private static instance: SoundControl;

  private fadeInterval?: Timeout;
  private fadeOutInProgress = false;

  public constructor(private sceneService: SceneService) {
    super();
    this.setDisplayName('SoundControl');
    this.listenState();

    const audio = new Audio();
    audio.id = 'sound-control';
    audio.autoplay = true;
    audio.loop = true;
    audio.volume = this.musicVolume;
    this._audioElement = audio;

    this.musicVolumeChanged.next(this.musicVolume);
  }

  public static getInstance(sceneService?: SceneService): SoundControl {
    if (!SoundControl.instance && sceneService) {
      SoundControl.instance = new SoundControl(sceneService);
    }
    return SoundControl.instance;
  }

  public get audioElement(): HTMLAudioElement {
    return this._audioElement;
  }

  public set audioElementSinkId(id: string) {
    void this._audioElement?.setSinkId?.(id);
  }

  public set eventHostVolume(v: number) {
    this._eventHostVolume = v;
  }
  public get eventHostVolume(): number {
    return this._eventHostVolume;
  }

  public set participantMaxVolume(v: number) {
    this._participantMaxVolume = v;
  }
  public get participantMaxVolume(): number {
    return this._participantMaxVolume;
  }
  public get musicVolume(): number {
    return this._musicVolume;
  }

  public set musicVolume(newVolume) {
    this._musicVolume = newVolume;
  }

  public get previousMusicVolume(): number {
    return this._previousMusicVolume;
  }

  public set previousMusicVolume(value) {
    this._previousMusicVolume = value;
  }

  public get musicAutoReducedForGroup(): boolean {
    return this._musicAutoReducedForGroup;
  }

  public set musicAutoReducedForGroup(value) {
    this._musicAutoReducedForGroup = value;
  }

  public async setAudioElementTrack(track: MediaStream): Promise<void> {
    this._audioElement.srcObject = track;
    this._audioElement.volume = this.convertVolumeLevel(this.musicVolume);
    this.musicVolumeChanged.next(this.musicVolume);

    try {
      this.info('Playing music');
      await this._audioElement.play();
    } catch (err) {
      this.error('Auto-play Error', err);
    }
  }

  public listenState(): void {
    SocialStateMachine.shared.stateChanged$.subscribe((state) => {
      this.isEventInProgress = SocialStateHelper.isAnyEventHappening(state);
    });
  }

  public async createAudioContext(): Promise<void> {
    if (this.audioContext) {
      await this.clearAudioContext();
    }
    const thisWindow = window as WindowWithWebkitAudio;
    thisWindow.AudioContext = thisWindow.AudioContext || thisWindow.webkitAudioContext;
    this.audioContext = new AudioContext();
  }

  public async clearAudioContext(): Promise<void> {
    if (this.audioContext) {
      try {
        await this.audioContext.close();
        this.audioContext = undefined;
      } catch (err) {
        this.warn('Error closing audio context', err);
      }
    }
  }

  public setAudioElement(audioElement: HTMLAudioElement): void {
    this._audioElement = audioElement;
  }

  public getPlayerVolumeByDistance({
    /** Enable new calculation for audio range*/
    enableAudioRange,
    /** The current user's player */
    mainPlayer,
    /** The user we're setting sound for */
    p,
  }: {
    enableAudioRange: boolean;
    mainPlayer: Player;
    p: Player;
  }): number {
    const { position } = p;
    const currPos = mainPlayer && mainPlayer.position;
    if (!currPos) {
      this.warn('Attempted to get player volume without position');
      return 0;
    }

    const distance = calculateDistance(currPos, position);
    let volume = 0;

    /* In past times, we calculated volume based on distance with the formula
     * Math.max(0, this.participantMaxVolume - (this.participantMaxVolume / max_distance) * distance)
     * but HTMLAudioVolume isn't sensitive enough to reflect that.
     * Instead we use more drastic volume changes so the difference it noticeable.
     */
    /*
      This flag was being used a inverse logic, since the behavior was
      if is true, don't use the linear volume logic.
      So to fix the behavior set on dev and prod,
      the new linear logic should works if the flag is enabled
    */
    if (enableAudioRange) {
      volume = this.setLinearVolume(mainPlayer, p, distance);
    } else {
      volume = this.calculateLinearVolume(mainPlayer, p, distance);
    }
    return volume;
  }

  private setLinearVolume(mainPlayer: Player, p: Player, distance: number): number {
    let volume = 0;
    // Set to DEFAULT MAX if they are in the same group
    const { groupId } = p.playerGroupInfo ?? {};
    if (groupId === mainPlayer.groupId && !mainPlayer.groupWarning && !p.groupWarning) {
      return (volume = DEFAULT_MAX_VOLUME);
    }

    // Set volume based on distance
    if (distance < MIN_DISTANCE_V1) {
      volume = mainPlayer.isInGroup ? NEAR_VOLUME : NO_GROUP_NEAR_VOLUME;
    } else if (distance < MAX_DISTANCE_V1) {
      volume = mainPlayer.isInGroup ? this.calculateMidDistanceVolume(distance) : NO_GROUP_MID_VOLUME;
    } else {
      volume = mainPlayer.isInGroup ? FAR_VOLUME : NO_GROUP_FAR_VOLUME;
    }

    return volume;
  }

  // Function to calculate volume based on position in MIN and MID range
  private calculateMidDistanceVolume(distance: number): number {
    const initialVolume = MID_VOLUME; // Initial volume level
    const startPosition = MIN_DISTANCE_V1; // Starting position of the zone
    const zoneWidth = MAX_DISTANCE_V1 - MIN_DISTANCE_V1; // Width of the zone

    // Calculate volume using the formula
    const volume = initialVolume - ((distance - startPosition) / zoneWidth) * initialVolume;

    // Ensure volume is not less than 0%
    return Math.max(volume, 0);
  }

  private calculateLinearVolume(mainPlayer: Player, p: Player, distance: number): number {
    if (mainPlayer.inSag !== p.inSag) {
      return 0;
    }

    // Set to DEFAULT MAX if they are in the same group
    const { groupId } = p.playerGroupInfo ?? {};
    if (groupId === mainPlayer.groupId && !mainPlayer.groupWarning) {
      return DEFAULT_MAX_VOLUME;
    }

    // Calculate volume based on distance
    const rawVolume = Math.max(mainPlayer.isInGroup ? -0.0613 * distance + 57 : -0.1818 * distance + 170, 0);
    const easingFunction = easing[this.sceneService.socialAudioEasingType as keyof typeof easing];
    const easedVolume = easingFunction(rawVolume / 100) * 100;
    const zoomFactor = 1 / this.sceneService.currentZoom;
    const adjustedVolume = this.sceneService.socialAudioRelative ? easedVolume * zoomFactor : easedVolume;

    return Math.min(90, adjustedVolume);
  }

  /** Manage volume updates and subscribers for non-presenting participants */
  public setParticipantVolume({
    p,
    playerIsAudience,
    setBy,
    volume,
  }: {
    p: Player;
    playerIsAudience?: boolean;
    setBy: string;
    volume: number;
  }): void {
    let volumeToSet = volume;
    if (this.isEventInProgress && playerIsAudience) {
      // Drop everyone else volume to max of 20% and set event speaker to 100%.  Any volume controlling like by movement would be maxed at 20%
      // if it's greater than the default that means performer is controlling crowd sound
      if (
        this.participantMaxVolume > DEFAULT_PARTICIPANT_MAX_VOLUME_PRESENTATION ||
        volume > this.participantMaxVolume
      ) {
        volumeToSet = volume === 0 ? 0 : this.participantMaxVolume;
      }
    }

    // Set Volume level to audioElement
    if (volumeToSet <= 100) {
      const volumeLevel = this.convertVolumeLevel(volumeToSet);
      this.setPlayerVolume({ p, volumeLevel, setBy });
    }
  }

  private setPlayerVolume({ p, setBy, volumeLevel }: { p: Player; volumeLevel: number; setBy?: string }): void {
    if (p.audioElement?.volume === volumeLevel || (!p.audioElement?.volume && !volumeLevel)) {
      return;
    }
    const playerId = prettyPlayerId(p.user);
    if (p.audioElement?.volume) {
      this.trace(
        `player ${playerId} setVolume request from ${p.audioElement?.volume} to ${volumeLevel} by ${
          setBy || 'unknown'
        }`,
      );
    }
    p.setVolume(volumeLevel);
  }

  public setEventSpeakerVolume({ newEventHostVolume, p }: { newEventHostVolume?: number; p: Player }): void {
    let eventHostVolume = this.eventHostVolume;
    if (newEventHostVolume !== undefined) {
      this.eventHostVolume = newEventHostVolume;
      eventHostVolume = newEventHostVolume;
    }
    const volumeLevel = this.convertVolumeLevel(eventHostVolume);

    this.setPlayerVolume({ p, volumeLevel, setBy: 'setEventSpeakerVolume' });
  }

  public setParticipantMaxVolume(v: number): void {
    this.participantMaxVolume = v;
  }
  // TODO: move business event logic into multimedia-room.service
  public setMusicVolume(volume: number, manualChange?: boolean): void {
    if (!(manualChange && this.fadeOutInProgress)) {
      if (this.audioElement && volume >= 0 && volume <= 100) {
        this.audioElement.volume = this.convertVolumeLevel(volume);
        this.musicVolume = volume;
        this.musicVolumeChanged.next(volume);
      }
    }
    //If the user manually changed the music volume, we register it so it can be kept when leaving a group
    if (manualChange) {
      this.previousMusicVolume = volume;
    }
  }

  public registerPreviousMusicVolume(musicVolume: number): void {
    this.previousMusicVolume = musicVolume;
  }

  /**Function to change the music volume in cases we want to reduce it only. */
  public reduceMusicVolume(volume: number, effect?: 'fade', isGroupLogic?: boolean): void {
    // In case of group logic, we want to set the music auto changed flag
    if (isGroupLogic) {
      this.musicAutoReducedForGroup = true;
    }
    if (volume < this.musicVolume) {
      this.registerPreviousMusicVolume(this.musicVolume);
      if (effect === 'fade') {
        this.fadeDownMusicVolume(volume);
        return;
      }
      this.setMusicVolume(volume);
    }
  }

  /**
   * Gradually reduces the music volume to the target level over a given duration.
   * @param {number} targetVolume - The volume level to fade down to (0-100).
   * @param {number} [duration=800] - The duration of the fade effect in milliseconds (default: 800ms).
   */
  private fadeDownMusicVolume(targetVolume: number, duration = 800) {
    this.fadeOutInProgress = true;
    const step = 50;
    const steps = duration / step;
    const volumeDiff = (this.musicVolume - targetVolume) / steps;

    clearInterval(this.fadeInterval); // Clear any existing fade operation

    this.fadeInterval = setInterval(() => {
      if (this.musicVolume <= targetVolume) {
        clearInterval(this.fadeInterval);
        this.setMusicVolume(targetVolume);
        this.fadeOutInProgress = false;
        return;
      }
      this.setMusicVolume(this.musicVolume - volumeDiff);
    }, step);
  }

  public restoreMusicVolumeAfterEvent(): void {
    if (this.audioElement) {
      this.setMusicVolume(this.previousMusicVolume);
    }
  }

  public restoreMusicVolumeAfterGroup(): void {
    // When leaving a group, we want to restore the previous music volume
    // If the music volume was changed manually while on group, we keep the new volume
    // Don't reset the music volume if an event is in progress
    if (!this.isEventInProgress) {
      if (this.audioElement && this.musicAutoReducedForGroup) {
        this.setMusicVolume(this.previousMusicVolume);
        this.musicAutoReducedForGroup = false;
      }
    }
  }

  public convertVolumeLevel(volumeLevel: number): number {
    return volumeLevel <= 0 ? 0 : volumeLevel / 100.0;
  }

  public reset(): void {
    this._eventHostVolume = DEFAULT_PRESENTER_VOLUME;
    this.participantMaxVolume = DEFAULT_PARTICIPANT_MAX_VOLUME_PRESENTATION;
  }

  public async destroy() {
    await this.clearAudioContext();
    clearInterval(this.fadeInterval);
    this.audioElement.srcObject = null;
  }
}
